/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-15 10:31:38
 **/

import { produce } from "immer";
import useDeviceManager from "./useDeviceManager";
import DeviceManager from "./devicemanager";
import has from "lodash-es/has";

export type PermissionEventCallback = {
	before_get_media_stream: (constratins: MediaStreamConstraints) => void;
	after_get_media_stream?: () => void;
};

export default class PermissionManager {
	static #instance: PermissionManager | null = null;
	public static get instance() {
		if (!PermissionManager.#instance) {
			PermissionManager.#instance = new PermissionManager();
		}
		return PermissionManager.#instance;
	}

	#device_status = new Map<string, PermissionState | null>();

	private constructor() {}

	public get device_status() {
		return this.#device_status;
	}

	public async request() {
		const permissions = await Promise.all([this.check("camera"), this.check("microphone")]);
		useDeviceManager.setState((state) =>
			produce(state, (draft) => {
				if (draft.camera.permission !== permissions[0].state) {
					draft.camera = {
						...draft.camera,
						permission: permissions[0].state
					};
				}
				if (draft.microphone.permission !== permissions[1].state) {
					draft.microphone = {
						...draft.microphone,
						permission: permissions[1].state
					};
				}
			})
		);
		return permissions;
	}

	private update_permission(name: string, state: PermissionState) {
		useDeviceManager.getState().set_permission(name as DeviceType.MediaDevicesName, state);
	}

	public async check(name: DeviceType.NewPermissionName) {
		// @ts-expect-error ...
		const result = await navigator.permissions.query({ name });
		if (!result.onchange && !this.#device_status.has(name)) {
			result.onchange = (ev: Event) => {
				const status = ev.target as PermissionStatus;
				this.#device_status.set(name, status.state);

				const current = useDeviceManager.getState()[name as DeviceType.MediaDevicesName];
				if (!has(current, "id")) {
					DeviceManager.instance.initialize().then(() => {
						this.update_permission(name, status.state);
					});
				} else if (current.permission !== status.state) {
					this.update_permission(name, status.state);
				}
			};
		}
		return result;
	}
}
